import { MAX_INCHES, MIN_INCHES, GUID_REGEX } from "@/utils/constants";
/**
 * This module contains the validators for the form fields.
 * It exports an object with the validators as properties.
 * Each validator is a function that takes a message as an argument and returns an object with the required properties ready to use in the rules array of a form field.
 *
 * Example usage:
 * ```
 * import { Validator } from "@/utils/validator";
 *
 * <Form.Item
 *    name="email"
 *    rules={[
 *      Validator.email("Please enter your email address"),
 *      Validator.minLength(6, "Please enter a valid email"),
 *      Validator.custom(value => value.includes("@"), "Please enter a valid email"),
 *    ]}
 * >
   <Input />
   </Form.Item>
 * ```
 */
// eslint-disable-next-line import/prefer-default-export
export const Validator = {
    // Validator for required fields
    required: (message = "This field is required") => ({
        required: true,
        message,
    }),
    // Validator for minimum length
    minLength: (minLength, message = "This field is too short") => ({
        min: minLength,
        message,
    }),
    // Validator for maximum length
    maxLength: (maxLength, message = "This field is too long") => ({
        max: maxLength,
        message,
    }),
    // Validator for email format
    email: (message = "Please enter a valid email") => [
        {
            required: true,
            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message,
        },
        Validator.maxLength(320),
    ],
    // Validator for number format
    number: (message = "Please enter a valid number") => ({
        validator: (_, value) => {
            if (value === undefined || value === null || value === "") {
                return Promise.resolve();
            }
            const num = Number(value);
            return !Number.isNaN(num) ? Promise.resolve() : Promise.reject(new Error(message));
        },
    }),
    // Validator for a range of numbers
    numberRange: (min, max, message = `Value must be between ${min} and ${max}`) => ({
        validator: (_, value) => {
            if (value === undefined || value === null || (value >= min && value <= max)) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(message));
        },
    }),
    // Validator with custom function
    custom: (validator, message = "Invalid value") => ({
        validator: (_, value) => (validator(value) ? Promise.resolve() : Promise.reject(new Error(message))),
    }),
    // Validator for password format
    password: () => [
        Validator.required("Password is required"),
        Validator.minLength(6, "Password must be at least 6 characters"),
        Validator.custom((value) => /[a-z]/.test(value), "Password must contain a lowercase letter"),
        Validator.custom((value) => /[A-Z]/.test(value), "Password must contain an uppercase letter"),
        Validator.custom((value) => /[0-9]/.test(value), "Password must contain a number"),
        Validator.custom((value) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value), "Password must contain a special character"),
    ],
    // Validator for exterior dimensions
    exteriorDimensions: (message = "Invalid dimension") => [
        Validator.required(message),
        Validator.custom(value => Number.isFinite(+value), message),
        Validator.numberRange(MIN_INCHES, MAX_INCHES, message),
    ],
    // Validator for GUID
    guid: (message = "Invalid GUID") => [Validator.custom(value => GUID_REGEX.test(value), message)],
    // Validator for optional fields but validate when they are present
    optional: (validatorFn, message = "Invalid value") => ({
        validator: async (_, value) => {
            if (value === undefined || value === null || value === "") {
                return Promise.resolve();
            }
            // Execute the validator function
            const rules = validatorFn(message);
            // Normalize to array
            const validators = Array.isArray(rules) ? rules : [rules];
            // Process all validators
            await Promise.all(validators.map(async (rule) => {
                const strErrorMessage = typeof rule.message === "string" ? rule.message || message : message;
                // Handle pattern validators
                if (rule.pattern && !rule.pattern.test(value)) {
                    throw new Error(strErrorMessage);
                }
                // Handle min/max
                if (rule.min !== undefined && value.length < rule.min) {
                    throw new Error(strErrorMessage);
                }
                if (rule.max !== undefined && value.length > rule.max) {
                    throw new Error(strErrorMessage);
                }
                // Handle custom validator
                if (rule.validator) {
                    await rule.validator(_, value, console.error);
                }
            }));
            return Promise.resolve();
        },
    }),
    // Validator for arrays containing a minimum number of items
    arrayMinLength: (minLength, message = `Please select at least ${minLength} item${minLength > 1 ? "s" : ""}`) => ({
        validator: (_, value) => {
            if (Array.isArray(value) && value.length >= minLength) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(message));
        },
    }),
};
